import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import blog1 from '../../assets/images/blog.png';

const blogPosts = [
  {
    id: 1,
    title: 'The Ultimate Guide to Social Media Lead Generation for Real Estate Agents',
    description: 'Provide a comprehensive overview of effective social media strategies for lead generation.',
    date: 'Aug, 21 2024',
    author: 'LeadTrek Admin',
    image: blog1,
  },
  {
    id: 2,
    title: 'How to Use AI to Boost Your Real Estate Lead Generation',
    description: 'Discuss the benefits of using AI-powered tools to automate lead generation tasks.',
    date: 'Aug, 21 2024',
    author: 'LeadTrek Admin',
    image: blog1,
  },
  {
    id: 3,
    title: '5 Essential Metrics to Track for Successful Lead Generation',
    description: 'Outline key performance indicators (KPIs) that real estate agents should monitor.',
    date: 'Aug, 21 2024',
    author: 'LeadTrek Admin',
    image: blog1,
  }
];

function BlogCard({ post, onClick }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      whileHover={{ y: -5 }}
      className="w-full max-w-[410px] rounded-2xl overflow-hidden bg-white/5 backdrop-blur-sm border border-white/10 hover:border-white/20 transition-all duration-300"
    >
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-r from-[#C334F6]/20 to-[#543DF4]/20 rounded-2xl blur-sm" />
        <img 
          src={post.image} 
          alt={post.title} 
          className="w-full h-[260px] object-cover rounded-t-2xl relative z-10"
        />
      </div>
      
      <div className="p-6">
        <div className="flex justify-between items-center mb-4 text-sm text-white/60">
          <span>{post.date}</span>
          <span>By {post.author}</span>
        </div>
        
        <h3 className="text-xl font-medium text-white mb-3 line-clamp-2">
          {post.title}
        </h3>
        
        <p className="text-white/70 mb-6 line-clamp-2">
          {post.description}
        </p>
        
        <button 
          onClick={onClick}
          className="w-full py-3 px-6 rounded-full bg-gradient-to-r from-[#C334F6] to-[#543DF4] text-white font-medium hover:opacity-90 transition-opacity"
        >
          Read More
        </button>
      </div>
    </motion.div>
  );
}

function Pagination() {
  return (
    <motion.div 
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      className="flex justify-center items-center space-x-4 mt-12"
    >
      <button className="w-10 h-10 rounded-full bg-gradient-to-r from-[#C334F6] to-[#543DF4] text-white flex items-center justify-center">
        1
      </button>
      {[2, 3, 4, 5].map((page) => (
        <button 
          key={page} 
          className="w-10 h-10 rounded-full text-white/60 hover:text-white hover:bg-white/10 transition-all duration-300 flex items-center justify-center"
        >
          {page}
        </button>
      ))}
      <button className="w-10 h-10 rounded-full border border-white/20 text-white/60 hover:text-white hover:border-white/40 transition-all duration-300 flex items-center justify-center">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M4.16666 10H15.8333" stroke="currentColor" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M10 4.16666L15.8333 10L10 15.8333" stroke="currentColor" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
    </motion.div>
  );
}

function Blog() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-black/95 to-black/90" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#C334F6]/5 to-[#543DF4]/5" />
      
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-20 -left-20 w-80 h-80 bg-[#C334F6]/10 rounded-full blur-3xl animate-float" />
        <div className="absolute -bottom-20 -right-20 w-80 h-80 bg-[#543DF4]/10 rounded-full blur-3xl animate-float-reverse" />
      </div>

      <div className="container mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="pt-32 pb-20 px-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="flex justify-center mb-8"
          >
            <span className="inline-block px-6 py-2 rounded-full border border-white/10 bg-white/5 backdrop-blur-sm text-white">
              Our Blog
            </span>
          </motion.div>

          <motion.h1 
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-5xl font-normal leading-[1.2] tracking-[0.001em] text-center text-white mb-6 max-w-[800px] mx-auto"
          >
            Stay Updated with Our
            <br />
            <span className="bg-gradient-to-r from-[#C334F6] to-[#543DF4] bg-clip-text text-transparent">
              Latest Insights
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="text-xl text-center text-white/80 mb-16 max-w-[600px] mx-auto"
          >
            Discover valuable tips, trends, and news in the real estate industry.
          </motion.p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 justify-items-center">
            {blogPosts.map(post => (
              <BlogCard 
                key={post.id} 
                post={post} 
                onClick={() => navigate(`/blog/${post.id}`)}
              />
            ))}
          </div>

          <Pagination />
        </motion.div>
      </div>
    </div>
  );
}

export default Blog;
